import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useBranding } from '../lib/branding.tsx';
import { ensureGoogleFonts } from '../lib/googleFonts.ts';
import { PRODUCT_NAME, displayBrandName } from './BrandMark.tsx';

export type NotFoundLink = { to: string; label: string };

/**
 * The "404 Page" design canvas: warm paper, a header rule carrying the lockup
 * and an ERROR 404 tag, a left-aligned statement block, and a footer rule.
 *
 * Geist is the canvas's face. Admin already ships it; portal and ops use the
 * system stack, so it is loaded here rather than through a Tailwind `font-*`
 * alias that means something different in each app — the same reasoning as
 * signInTheme.ts's inline families.
 */
const SANS = "Geist, system-ui, -apple-system, 'Segoe UI', sans-serif";
const MONO = "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const C = {
  paper: '#F7F6EF',
  ink: '#1D1C17',
  body: '#4A483F',
  muted: '#6B695E',
  rule: '#E3E1D6',
  eyebrow: '#1F5C45',
  chipDot: '#7FC4A2',
} as const;

/**
 * The 404 page. `links` is REQUIRED and supplied by each app, never defaulted
 * here: one component serves admin, ops and three portal hosts, and the only
 * honest "home" is one the router of THAT app on THAT host can reach. The
 * hard-coded `/admin` + `/c` pair this replaced was right on almost none of
 * them — `/admin` inside the portal bundle is itself a 404, and `/c` on the
 * driver host is too.
 *
 * WHAT THE CANVAS HAD THAT THIS DOES NOT. Its footer is a
 * `mailto:support@supplierclear.com` — an address that appears nowhere else in
 * the product, so it is not shipped as a promise of help. The footer carries
 * the requested path instead, which is what anyone reporting a broken link
 * needs to quote. The canvas also dropped the way home entirely while its copy
 * still said "Head back to a home screen"; the links are that way home.
 */
export function NotFound({ links }: { links: readonly NotFoundLink[] }) {
  const { branding } = useBranding();
  const { pathname } = useLocation();
  useEffect(() => {
    ensureGoogleFonts(
      'l2u-notfound-fonts',
      'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600&family=Geist+Mono:wght@400;500&display=swap',
    );
  }, []);

  const brandName = displayBrandName(branding.companyName);
  const gutter = 'px-[clamp(20px,4vw,48px)]';

  return (
    <div
      className="flex min-h-screen flex-col antialiased"
      style={{ background: C.paper, color: C.ink, fontFamily: SANS }}
    >
      <header
        className={`flex items-center justify-between gap-4 py-6 ${gutter}`}
        style={{ borderBottom: `1px solid ${C.rule}` }}
      >
        {/* Tenant logo first, as everywhere else; the canvas's chip + name is
            the fallback. Not <BrandMark>: its fill is the product green and the
            canvas draws the inverted ink chip. */}
        {branding.logoUrl ? (
          <img src={branding.logoUrl} alt={brandName} className="h-7 w-auto" />
        ) : (
          <div className="flex min-w-0 items-center gap-2.5">
            <div
              className="flex flex-none items-center justify-center"
              style={{ width: 22, height: 22, borderRadius: 5, background: C.ink }}
              aria-hidden="true"
            >
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.chipDot }} />
            </div>
            <span
              className="truncate"
              style={{ fontWeight: 600, fontSize: 16, letterSpacing: '-0.01em' }}
            >
              {brandName}
            </span>
          </div>
        )}
        <span
          className="flex-none"
          style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.08em', color: C.muted }}
        >
          ERROR 404
        </span>
      </header>

      <main className={`flex flex-1 items-center justify-center py-16 ${gutter}`}>
        <div className="flex w-full max-w-[560px] flex-col gap-8">
          <div className="flex flex-col gap-4">
            <span
              style={{ fontFamily: MONO, fontSize: 12, letterSpacing: '0.08em', color: C.eyebrow }}
            >
              PAGE NOT FOUND
            </span>
            <h1
              className="m-0"
              style={{
                fontSize: 'clamp(32px, 5vw, 48px)',
                lineHeight: 1.05,
                letterSpacing: '-0.03em',
                fontWeight: 600,
                textWrap: 'balance',
              }}
            >
              This page isn’t on the register.
            </h1>
            <p
              className="m-0"
              style={{ fontSize: 17, lineHeight: 1.55, color: C.body, textWrap: 'pretty' }}
            >
              The link may be out of date, or the page has moved. Head back to a home screen and
              carry on from there.
            </p>
          </div>

          {links.length > 0 && (
            <nav aria-label="Ways back" className="flex flex-wrap gap-x-6 gap-y-3">
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  // The tenant's colour, as every other link in the product —
                  // the canvas's #1F5C45 is only the fallback.
                  className="font-semibold no-underline hover:underline"
                  style={{ fontSize: 15, color: `var(--brand-primary, ${C.eyebrow})` }}
                >
                  {link.label} <span aria-hidden="true">→</span>
                </Link>
              ))}
            </nav>
          )}
        </div>
      </main>

      <footer
        className={`flex flex-wrap justify-between gap-x-6 gap-y-2 py-5 ${gutter}`}
        style={{ borderTop: `1px solid ${C.rule}`, fontSize: 13, color: C.muted }}
      >
        <span className="min-w-0 break-all" style={{ fontFamily: MONO, fontSize: 12 }}>
          {pathname}
        </span>
        <span>
          © {new Date().getFullYear()} {PRODUCT_NAME}
        </span>
      </footer>
    </div>
  );
}
